/**
 * Google 계정 연결 UI (DESIGN 14.105).
 *
 * Google 버튼은 공식 GIS가 직접 그린다. 겉모양만 흉내 낸 버튼은 피싱 화면과 구분할
 * 수 없고, 브라우저 정책이 바뀌면 인증 흐름도 깨진다. 서버 설정이 없으면 스크립트도
 * 받지 않고 시작 화면 연결 영역을 숨겨 게스트의 10초 진입선을 보존한다. 게임 안 계정
 * 화면은 기능이 준비 중이라고만 말하고 플레이를 막지 않는다.
 */
import { S, $ } from '../core/state.js';
import { RelayCancelled, clearRelayPending, openRelayWindow, runAuthRelay, takeRelayReturn } from './auth-relay.js';

let changed = null;
let config = null;
let scriptPromise = null;
let preparePromise = null;
let initialized = false;
let prepareToken = null;
let googleGeneration = 0;
/** 같은 창으로 돌아온 Google 중계의 결과 문장 — 뒤이은 첫 설정 읽기(prepare)가 덮어쓰지 않게 한 번 더 건다. */
let bootNote = null;

/**
 * R211(레드팀 C2-18) — 시작 화면의 버튼은 어두운 중간 크기다(`filled_black`·`medium`, Google 공식 테마). 흰 큰 버튼이 카드에서
 * 가장 밝아 처음 온 사람이 「로그인이 필요한 게임」으로 읽었다 — 주 행동은 「게임 시작」이고 연결은 선택 사항이다.
 * 게임 안 계정 창은 연결하러 들어온 자리라 그대로 흰 큰 버튼이다.
 */
const SURFACES = [
  { button: '#googlebtn', note: '#authnote', logout: '#authlogout', theme: 'filled_black', size: 'medium' },
  { button: '#accountgooglebtn', note: '#accountauthnote', logout: '#accountauthlogout' },
];

const token = () => {
  try { return localStorage.getItem('unige_token') || ''; } catch { return ''; }
};

/**
 * 10-20(사용자 신고 「구글 계정 전환도 안되고」 — 크롬에서는 됨) — 팝업을 막는 브라우저에서 공식 Google 버튼은 로그인 창을 못 열고
 * 아무 말 없이 멈췄다(TEST 재현: 콘솔에 `[GSI_LOGGER] Failed to open popup window` 만). Google 스크립트가 이 페이지에서 여는 창이 막히면
 * 그 사실과 할 일을 계정 칸에 적는다. 막히지 않은 창은 손대지 않고 그대로 돌려준다.
 */
function watchGooglePopup() {
  if (window.__unigeGooglePopupWatch || typeof window.open !== 'function') return;
  window.__unigeGooglePopupWatch = true;
  const open = window.open;
  window.open = function (...args) {
    const opened = open.apply(this, args);
    if (!opened && /^https:\/\/accounts\.google\.com\//.test(String(args[0] ?? ''))) {
      note('브라우저가 Google 로그인 창(팝업)을 막았다. 주소창의 팝업 차단 표시에서 이 사이트를 허용한 뒤 다시 눌러 달라. 앱 안 브라우저라면 크롬 같은 일반 브라우저에서 열어 달라.', true);
    }
    return opened;
  };
}

function loadGis() {
  watchGooglePopup();
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  const loading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Google 로그인 화면을 불러오지 못했다.'));
    document.head.appendChild(s);
  });
  // 광고 차단기·일시 네트워크 오류 뒤 패널을 다시 열면 한 번 더 시도할 수 있어야 한다.
  scriptPromise = loading.catch((e) => { scriptPromise = null; throw e; });
  return scriptPromise;
}

function publish(patch) {
  const moved = Object.entries(patch).some(([k, v]) => S.auth[k] !== v);
  Object.assign(S.auth, patch);
  if (moved) window.dispatchEvent(new CustomEvent('unige:auth-status'));
}

function note(text, bad = false) {
  const moved = S.auth.message !== text || S.auth.bad !== bad;
  S.auth.message = text;
  S.auth.bad = bad;
  for (const s of SURFACES) {
    const el = $(s.note);
    if (!el) continue;
    el.textContent = text;
    el.classList.toggle('bad', bad);
  }
  if (moved) window.dispatchEvent(new CustomEvent('unige:auth-status'));
}

/** 공식 GIS 버튼은 시작 화면과 계정 패널에서 이 한 함수로만 그린다. */
function paintSurface(s) {
  const noteEl = $(s.note);
  if (noteEl) {
    noteEl.textContent = S.auth.message;
    noteEl.classList.toggle('bad', !!S.auth.bad);
  }
  const logout = $(s.logout);
  if (logout) logout.style.display = S.auth.linked || S.naverAuth?.linked ? 'inline-block' : 'none';
  const button = $(s.button);
  if (!button || !config?.enabled) return;
  button.textContent = '';
  /**
   * 연결 뒤에도 공식 GIS 로그인 버튼을 다시 그리면 화면은 동시에
   * "연결 완료"와 "로그인"을 말한다. 사용자가 어느 상태인지 추측하게 만들지
   * 않는다. 연결 상태에서는 누를 수 있는 로그인 버튼 대신 완료 배지만 남긴다.
   * 계정을 바꾸려면 바로 아래의 명시적인 기기 로그아웃을 거친다.
   */
  button.classList.toggle('linked', !!S.auth.linked);
  if (S.auth.linked) {
    const badge = document.createElement('span');
    badge.className = 'googlelinked';
    badge.setAttribute('role', 'status');
    badge.textContent = '✓ Google 계정 연결됨';
    button.appendChild(badge);
    return;
  }
  if (config.relay) {
    /**
     * 등록 안 된 주소(DESIGN 14.448) — 공식 GIS 버튼은 등록된 주소의 새 창이 그린다. 여기 버튼은 그 창을 여는 문이라
     * Google 버튼 모양을 흉내 내지 않는다(14.105 — 흉내 낸 버튼은 피싱 화면과 구분이 안 된다).
     */
    const door = document.createElement('button');
    door.type = 'button';
    door.className = 'googlerelay';
    door.textContent = 'Google 계정으로 로그인';
    door.onclick = startGoogleRelay;
    button.appendChild(door);
    return;
  }
  if (!initialized) return;
  google.accounts.id.renderButton(button, {
    type: 'standard', theme: s.theme || 'outline', size: s.size || 'large', width: 280,
    text: 'signin_with', shape: 'rectangular',
  });
}

function paintAll() {
  for (const s of SURFACES) paintSurface(s);
}

async function exchange(credential, mode = 'link', startedToken = token()) {
  return linkGoogle((m) => fetch('/api/auth/google', {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ credential, token: token() || undefined, mode: m }),
  }), mode, startedToken);
}

/**
 * 중계로 받은 일회용 코드를 계정 열쇠로 바꾼다(DESIGN 14.448). 연결·복구·전환 확인·열쇠 교체·캐릭터 목록 갱신은 직접 로그인
 * (`exchange`)과 같은 `linkGoogle` 한 길이다. 전환 확인 뒤에는 같은 코드로 `switch` 를 다시 낸다(서버가 확정 전 점유를 돌려놓는다).
 */
async function exchangeRelay(code, verifier, mode = 'link', startedToken = token()) {
  return linkGoogle((m) => fetch('/api/auth/google/relay/redeem', {
    method: 'POST', headers: { 'content-type': 'application/json', ...(token() ? { 'x-unige-token': token() } : {}) },
    body: JSON.stringify({ code, verifier, mode: m }),
  }), mode, startedToken);
}

async function linkGoogle(send, mode, startedToken) {
  if (token() !== startedToken) throw new Error('다른 창에서 계정이 바뀌었다. 현재 계정을 확인한 뒤 다시 로그인해 달라.');
  const r = await send(mode);
  const j = await r.json().catch(() => ({}));
  if (token() !== startedToken) throw new Error('다른 창에서 계정이 바뀌었다. 현재 계정을 확인한 뒤 다시 로그인해 달라.');
  if (r.status === 409 && j.code === 'ACCOUNT_SWITCH_REQUIRED' && mode !== 'switch') {
    const yes = confirm(
      '현재 게스트 계정과 이 Google 계정에 연결된 유니지 계정이 다르다.\n\n'
      + '두 계정의 캐릭터와 아이템은 합쳐지지 않는다. Google 계정에 연결된 캐릭터로 전환할까?\n'
      + '취소하면 현재 캐릭터를 그대로 유지한다.',
    );
    if (yes) return linkGoogle(send, 'switch', startedToken);
    note('전환을 취소했다. 현재 캐릭터는 그대로다.');
    return;
  }
  if (!r.ok || !j.token) throw new Error(j.error || 'Google 계정을 연결하지 못했다.');

  localStorage.setItem('unige_token', j.token);
  // 중계로 돌아온 첫 부팅(팝업 차단)은 아직 설정을 읽기 전이다 — 열쇠만 바꾸고, 뒤이은 초기화가 새 열쇠로 상태를 읽는다.
  if (config) config.linked = true;
  publish({ linked: true, bad: false, message: j.restored
    ? 'Google 계정에 연결된 캐릭터를 불러왔다.'
    : '연결 완료 · 다른 기기에서도 같은 Google 계정으로 이어할 수 있다.' });
  paintAll();
  void prepareNaver(true).catch(() => {});
  if (changed) await changed(j);
}

async function credential(result) {
  note('Google 계정을 확인하는 중…');
  try { await exchange(String(result?.credential ?? '')); }
  catch (e) { note(e.message || 'Google 계정을 연결하지 못했다.', true); }
}

/**
 * 등록 안 된 주소의 Google 로그인(DESIGN 14.448) — 누름 안에서 첫 await 전에 창부터 연다(팝업 허용은 누름에 묶여 있다).
 * 막히면 같은 창으로 다녀오고, 돌아오면 `finishGoogleRelay` 가 이어 받는다.
 */
function startGoogleRelay() {
  if (!config?.relay) return;
  const popup = openRelayWindow();
  void googleRelayLogin(popup, config.relay);
}

async function googleRelayLogin(popup, relay) {
  const startedToken = token();
  note('새 창에서 Google 로그인을 마쳐 달라…');
  try {
    const fingerprint = await accountFingerprint(startedToken);
    const { code, verifier } = await runAuthRelay(popup, { provider: 'google', relay, fingerprint });
    note('Google 계정을 확인하는 중…');
    await exchangeRelay(code, verifier, 'link', startedToken);
  } catch (e) {
    if (e instanceof RelayCancelled) note(e.message);
    else note(e.message || 'Google 계정을 연결하지 못했다.', true);
  }
}

/** 팝업이 막혀 같은 창으로 다녀온 Google 중계 — main 의 첫 캐릭터 조회보다 먼저 끝낸다(네이버 복귀와 같은 자리). */
export async function finishGoogleRelay() {
  const back = takeRelayReturn('google');
  if (!back) return;
  const url = new URL(location.href);
  url.searchParams.delete('auth');
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  const keep = (text, bad) => { note(text, bad); bootNote = { text, bad }; };
  if (back.error) { keep(back.error, true); return; }
  const startedToken = token();
  if (back.fingerprint !== await accountFingerprint(startedToken)) {
    keep('로그인을 시작한 계정과 현재 계정이 다르다. 현재 계정으로 다시 로그인해 달라.', true);
    return;
  }
  note('Google 계정을 확인하는 중…');
  try { await exchangeRelay(back.code, back.verifier, 'link', startedToken); if (!S.auth.linked) keep(S.auth.message, false); }
  catch (e) { keep(e.message || 'Google 계정을 연결하지 못했다.', true); }
}

async function readConfig(expectedToken) {
  const headers = {};
  if (expectedToken) headers['x-unige-token'] = expectedToken;
  const r = await fetch('/api/auth/google/config', { headers });
  if (!r.ok) throw new Error(`Google 연결 정보를 불러오지 못했다. 잠시 뒤 계정 창을 다시 열어 달라.`);
  return r.json();
}

async function prepare() {
  const initial = token();
  if (preparePromise && prepareToken === initial) return preparePromise;
  prepareToken = initial;
  const generation = ++googleGeneration;
  const current = () => generation === googleGeneration && token() === initial;
  const loading = (async () => {
    const next = await readConfig(initial);
    if (!current()) return false;
    config = next;
    // 등록 안 된 주소는 clientId 대신 relay(등록된 주소)를 받는다 — 그 새 창에서 로그인한다(14.448). GIS 는 직접 그릴 때만 받는다.
    const enabled = !!(config.enabled && (config.clientId || config.relay));
    publish({ ready: true, enabled, relay: config.relay || '', linked: !!config.linked, bad: false,
      message: enabled
        ? (config.linked
          ? 'Google 연결됨 · 다른 기기에서도 같은 캐릭터를 이어한다.'
          : config.relay
            ? '선택 사항 · 새 창에서 Google 로그인을 마치면 이 화면으로 돌아온다.'
            : '선택 사항 · 연결하면 다른 기기에서도 캐릭터를 이어할 수 있다.')
        : 'Google 계정 연동을 준비 중이다.' });
    if (bootNote) { note(bootNote.text, bootNote.bad); bootNote = null; }
    if (!enabled || !config.clientId) return true;
    await loadGis();
    if (!current()) return false;
    if (!initialized) {
      google.accounts.id.initialize({ client_id: config.clientId, callback: credential });
      initialized = true;
    }
    return true;
  })();
  preparePromise = loading;
  try { return await loading; }
  catch (e) { if (!current()) return false; if (preparePromise === loading) preparePromise = null; throw e; }
}

/** 게임 안 `계정` 패널은 innerHTML 뒤에 공식 버튼을 다시 붙여야 한다. */
export async function mountGoogleAuthPanel() {
  try {
    if (!await prepare()) return;
    paintSurface(SURFACES[1]);
  } catch (e) {
    publish({ ready: true, bad: true, message: e.message || 'Google 연결을 준비하지 못했다. 잠시 뒤 다시 시도해 달라.' });
  }
}

/** Google 연결을 지우는 것이 아니라 이 브라우저의 유니지 계정 열쇠만 지운다. */
export function logoutGoogleDevice() {
  if (!S.auth.linked && !S.naverAuth?.linked) return;
  if (!confirm('이 기기에서 로그아웃할까? 연결한 계정과 캐릭터 기록은 유지된다.')) return;
  window.google?.accounts?.id?.disableAutoSelect();
  localStorage.removeItem('unige_token');
  clearNaverPending();
  clearRelayPending();
  window.dispatchEvent(new CustomEvent('unige:auth-device-logout'));
  location.reload();
}

export async function initGoogleAuth(onChanged) {
  changed = onChanged;
  const startLogout = $('#authlogout');
  if (startLogout) startLogout.onclick = logoutGoogleDevice;
  try {
    if (!await prepare()) return;
    if (!config?.enabled || !(config.clientId || config.relay)) return;
    $('#authbox').style.display = 'block';
    $('#authbox').classList.remove('auth-failed');
    paintSurface(SURFACES[0]);
  } catch (e) {
    /**
     * 게스트 시작은 인증 공급자 장애와 분리한다. 영역 안에서만 이유를 말한다.
     * R171(레드팀 C27) — 광고 차단기가 Google 스크립트를 막으면 첫 화면 맨 위에 **빈 40px 상자와 붉은 오류**가
     * 떴다. 막힌 것은 로그인 하나뿐이고 게임은 그대로 시작된다 — 상자를 접고 그 사실만 한 줄로 말한다(.auth-failed).
     * 붉은 글씨(bad)는 쓰지 않는다. 계정 창(게임 안)은 이 문장을 그대로 보여 주고, 열 때 다시 시도한다(loadGis).
     */
    const box = $('#authbox');
    if (box) { box.style.display = 'block'; box.classList.add('auth-failed'); }
    publish({ ready: true, bad: false, message: 'Google 로그인은 지금 안 된다 · 바로 시작할 수 있다' });
    paintSurface(SURFACES[0]);
  }
}

// OAuth 코드는 서버 callback만 받는다. 클라이언트는 같은 탭의 완료 표식과
// HttpOnly cookie로 finish하고, 계정 열쇠는 요청 헤더/응답 본문 밖으로 보내지 않는다.
const NAVER_PENDING = 'unige.naver.pending';
const NAVER_SURFACES = [
  { box: '#naverbox', button: '#naverbtn', note: '#navernote', retry: '#naverretry', unlink: '#naverunlink' },
  { button: '#accountnaverbtn', note: '#accountnavernote', retry: '#accountnaverretry', unlink: '#accountnaverunlink' },
];
let naverPrepare = null;
let naverPrepareToken = null;
let naverGeneration = 0;
let naverFinishing = null;

function naverPublish(patch) {
  const moved = Object.entries(patch).some(([k, v]) => S.naverAuth[k] !== v);
  Object.assign(S.naverAuth, patch);
  paintNaver();
  if (moved) window.dispatchEvent(new CustomEvent('unige:auth-status'));
}

function paintNaver() {
  const a = S.naverAuth;
  for (const s of NAVER_SURFACES) {
    const box = s.box && $(s.box);
    if (box) box.style.display = a.enabled || a.busy || a.bad || a.retry || a.linked ? 'block' : 'none';
    const button = $(s.button);
    if (button) {
      const label = a.busy ? '네이버 계정 확인 중…' : a.linked ? '✓ 네이버 계정 연결됨' : token() ? '현재 계정에 네이버 연결' : '네이버로 로그인';
      button.setAttribute('aria-label', label);
      if (a.busy || a.linked) button.textContent = label;
      else if (!button.querySelector('img')) {
        button.textContent = '';
        const img = document.createElement('img'); img.src = '/assets/auth/naver-login-green.png'; img.alt = '네이버 로그인';
        button.append(img);
      }
      button.disabled = !a.enabled || a.busy || a.linked;
      button.onclick = startNaverAuth;
    }
    const text = $(s.note);
    if (text) { text.textContent = a.message; text.classList.toggle('bad', a.bad); }
    const retry = $(s.retry);
    if (retry) { retry.hidden = !a.retry; retry.disabled = a.busy; retry.onclick = () => void finishNaverAuth(true); }
    const unlink = $(s.unlink);
    if (unlink) { unlink.hidden = !a.linked; unlink.disabled = !a.unlinkEnabled || a.busy; unlink.onclick = unlinkNaverAuth; }
  }
  const logout = $('#authlogout');
  if (logout) logout.style.display = S.auth.linked || a.linked ? 'inline-block' : 'none';
  // 네이버만 연결한 기기에서도 공통 로그아웃을 제공한다.
  if (a.linked && $('#authbox')) $('#authbox').style.display = 'block';
}

async function accountFingerprint(value) {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(bytes)].map(b => b.toString(16).padStart(2, '0')).join('');
}

async function naverRequest(path, body, expectedToken = token()) {
  if (token() !== expectedToken) throw new Error('다른 창에서 계정이 바뀌었다. 현재 계정으로 다시 로그인해 달라.');
  const headers = { 'content-type': 'application/json' };
  if (expectedToken) headers['x-unige-token'] = expectedToken;
  const response = await fetch('/api/auth/naver/' + path, { method: 'POST', headers, body: JSON.stringify(body) });
  const data = await response.json().catch(() => ({}));
  if (token() !== expectedToken) throw new Error('다른 창에서 계정이 바뀌었다. 현재 계정으로 다시 로그인해 달라.');
  return { response, data };
}

async function beginNaver(path, body = {}) {
  const relay = S.naverAuth.relay;
  // 콜백이 등록 안 된 주소(14.448)는 등록된 주소의 새 창에서 시작·콜백을 끝낸다 — 창은 누름 안에서 첫 await 전에 연다.
  const popup = relay ? openRelayWindow() : null;
  naverPublish({ busy: true, bad: false, retry: false, notice: false, message: relay ? '새 창에서 네이버 로그인을 마쳐 달라…' : '네이버 로그인 화면으로 이동하는 중…' });
  try {
    const initial = token();
    const fingerprint = await accountFingerprint(initial);
    if (token() !== initial) throw new Error('계정이 바뀌었다. 현재 계정으로 다시 로그인해 달라.');
    sessionStorage.setItem(NAVER_PENDING, JSON.stringify({ fingerprint, at: Date.now() }));
    if (relay) {
      const { code, verifier } = await runAuthRelay(popup, { provider: 'naver', relay, purpose: path === 'unlink' ? 'unlink' : 'login', allowGuest: body.allowGuest === true, fingerprint });
      await redeemNaver(code, verifier, initial);
      naverPublish({ busy: false });
      await finishNaverAuth(true);
      return;
    }
    const { response, data } = await naverRequest(path, body, initial);
    if (!response.ok) throw new Error(data.error || '네이버 로그인을 시작하지 못했다. 잠시 뒤 다시 시도해 달라.');
    const url = new URL(data.url);
    if (url.origin !== 'https://nid.naver.com' || url.pathname !== '/oauth2.0/authorize') throw new Error('네이버 로그인 주소를 확인하지 못했다. 다시 시도해 달라.');
    location.assign(url.href);
  } catch (e) {
    clearNaverPending();
    if (e instanceof RelayCancelled) { naverPublish({ busy: false, bad: false, notice: true, message: e.message }); return; }
    naverPublish({ busy: false, bad: true, message: e instanceof TypeError ? '네이버 로그인에 연결하지 못했다. 잠시 뒤 다시 시도해 달라.' : e.message || '네이버 로그인을 시작하지 못했다.' });
  }
}

/** 중계로 받은 일회용 코드를 내고, 그 거래를 이 브라우저·현재 계정에 옮겨 묶는다(서버가 이 출처 쿠키를 굽는다). 이어서 같은 finish. */
async function redeemNaver(code, verifier, expectedToken) {
  const { response, data } = await naverRequest('relay/redeem', { code, verifier }, expectedToken);
  if (!response.ok) throw new Error(data.error || '네이버 로그인 결과를 받지 못했다. 다시 시작해 달라.');
}

export async function startNaverAuth() {
  if (S.naverAuth.busy || !S.naverAuth.enabled || S.naverAuth.linked) return;
  await beginNaver('start');
}

function clearNaverPending() {
  try { sessionStorage.removeItem(NAVER_PENDING); } catch { /* 저장 불가 환경에서도 원래 계정 선택으로 복귀 */ }
}

function clearNaverCallback() {
  const url = new URL(location.href);
  if (url.searchParams.get('auth') !== 'naver') return;
  url.searchParams.delete('auth');
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
}

/** main의 첫 캐릭터 조회보다 먼저 완료한다. 지연 동안 시작 버튼도 잠긴다. */
export async function finishNaverAuth(retry = false) {
  if (naverFinishing) return naverFinishing;
  if (!retry && new URL(location.href).searchParams.get('auth') !== 'naver') return;
  naverFinishing = (async () => {
    naverPublish({ busy: true, retry: false, bad: false, message: '네이버 계정을 확인하는 중…' });
    let retain = false;
    let completed = false;
    try {
      // 팝업이 막혀 같은 창으로 다녀온 중계(14.448) — 돌아온 주소의 조각에서 코드를 꺼내 먼저 교환한다.
      const back = retry ? null : takeRelayReturn('naver');
      if (back?.error) throw new Error(back.error);
      const pending = JSON.parse(sessionStorage.getItem(NAVER_PENDING) || 'null');
      const initial = token();
      if (!pending || pending.fingerprint !== await accountFingerprint(initial)) throw new Error('로그인을 시작한 계정과 현재 계정이 다르다. 현재 계정으로 다시 로그인해 달라.');
      if (back) await redeemNaver(back.code, back.verifier, initial);
      let result = await naverRequest('finish', {}, initial);
      if (result.response.status === 409 && result.data.code === 'ACCOUNT_SWITCH_REQUIRED') {
        const yes = confirm('현재 계정과 이 네이버 계정에 연결된 유니지 계정이 다르다.\n\n캐릭터와 아이템은 합쳐지지 않는다. 네이버에 연결된 계정으로 전환할까?\n취소하면 현재 계정을 유지한다.');
        if (!yes) { naverPublish({ notice: true, message: '전환을 취소했다. 현재 계정은 그대로다.' }); return; }
        result = await naverRequest('finish', { mode: 'switch' }, initial);
      }
      if (!result.response.ok || !result.data.token) {
        retain = result.data.code === 'OAUTH_PENDING' || result.response.status >= 500;
        throw new Error(result.data.error || '네이버 연결을 완료하지 못했다. 다시 시도해 달라.');
      }
      localStorage.setItem('unige_token', result.data.token);
      completed = true;
      clearNaverPending();
      naverPublish({ linked: !result.data.unlinked, bad: false, notice: true, message: result.data.unlinked ? '네이버 연결을 해제했다. 캐릭터 기록과 이 기기의 접속은 유지된다.' : result.data.restored ? '네이버에 연결된 캐릭터를 불러왔다.' : '네이버 연결 완료 · 다른 기기에서도 같은 계정으로 이어할 수 있다.' });
      config = null; preparePromise = null; googleGeneration++;
      naverGeneration++; naverPrepare = null;
      if (result.data.token !== initial) publish({ ready: false, enabled: false, linked: false, bad: false, message: '현재 계정의 Google 연결을 확인하는 중…' });
      if (changed) { await changed(result.data); await initGoogleAuth(changed); await prepareNaver(true); }
    } catch (e) {
      // 응답 유실은 같은 거래로 재시도한다. 계정 불일치/취소/만료는 다시 시작한다.
      if (!completed && e instanceof TypeError) retain = true;
      naverPublish({ bad: true, retry: retain, message: completed ? '네이버 처리는 완료했다. 계정 창을 다시 열어 연결 상태를 확인해 달라.' : e instanceof TypeError ? '연결 결과를 받지 못했다. 다시 확인을 눌러 이어서 진행해 달라.' : e.message || '네이버 연결을 확인하지 못했다.' });
    } finally {
      clearNaverCallback();
      if (!retain) clearNaverPending();
      naverPublish({ busy: false });
    }
  })();
  try { await naverFinishing; } finally { naverFinishing = null; }
}

async function prepareNaver(force = false) {
  const initial = token();
  if (!force && naverPrepare && naverPrepareToken === initial) return naverPrepare;
  naverPrepareToken = initial;
  const generation = ++naverGeneration;
  const current = () => generation === naverGeneration && token() === initial;
  const loading = (async () => {
    const headers = initial ? { 'x-unige-token': initial } : {};
    const r = await fetch('/api/auth/naver/status', { headers });
    const j = await r.json().catch(() => ({}));
    if (!current()) return;
    if (!r.ok) throw new Error(j.error || '네이버 연결 정보를 불러오지 못했다. 계정 창을 다시 열어 달라.');
    naverPublish({ ready: true, enabled: !!j.enabled, relay: typeof j.relay === 'string' ? j.relay : '', linked: !!j.linked, unlinkEnabled: !!j.unlinkEnabled, canUnlink: !!j.canUnlink, unlinkReason: String(j.unlinkReason || ''),
      ...(!S.naverAuth.notice && !S.naverAuth.bad && !S.naverAuth.retry && !S.naverAuth.busy ? { message: j.linked ? '네이버 연결됨 · 다른 기기에서도 같은 캐릭터를 이어한다.' : j.enabled ? '선택 사항 · 네이버 계정을 연결해 캐릭터를 보관해 달라.' : '네이버 계정 연동을 준비 중이다.' } : {}) });
  })();
  naverPrepare = loading;
  try { await loading; } catch (e) { if (!current()) return; if (naverPrepare === loading) naverPrepare = null; naverPublish({ ready: true, bad: true, message: e instanceof TypeError ? '네이버 연결 정보를 받지 못했다. 계정 창을 다시 열어 달라.' : e.message }); }
}

export async function initNaverAuth(onChanged) { changed = onChanged; await prepareNaver(); paintNaver(); }
export async function mountNaverAuthPanel() { paintNaver(); await prepareNaver(); paintNaver(); }

export async function unlinkNaverAuth() {
  if (!S.naverAuth.linked || S.naverAuth.busy) return;
  if (!S.naverAuth.unlinkEnabled) { naverPublish({ message: '네이버 연결 해제를 준비 중이다. 기기 로그아웃은 연결과 기록을 유지한다.' }); return; }
  const allowGuest = !S.naverAuth.canUnlink;
  const question = allowGuest
    ? '네이버 연결을 해제하면 복구할 수 있는 연결 계정이 없어진다. 이 브라우저의 데이터를 지우면 캐릭터를 다시 찾지 못할 수 있다.\n\n복구 연결 없이 해제할까? 현재 캐릭터와 게임 기록은 삭제되지 않는다.'
    : '네이버 연결을 해제할까? 캐릭터와 다른 계정 연결은 유지된다.';
  if (!confirm(question)) return;
  await beginNaver('unlink', allowGuest ? { allowGuest: true } : {});
}
